import React from 'react';

interface BankerButtonProps {
  isActive: boolean;
  onToggle: () => void;
  tooltipText: React.ReactNode;
}

const BriefcaseIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
    <rect
      x="3"
      y="7"
      width="18"
      height="13"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M3 12h18"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M10 12v1.5a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V12"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const BankerButton: React.FC<BankerButtonProps> = ({ isActive, onToggle, tooltipText }) => (
  <div className="flex flex-col items-center pb-2">
    <div className="relative group">
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={isActive}
        aria-describedby={isActive ? 'banker-explanation' : undefined}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border-2 shadow-sm hover:scale-105 active:scale-95 ${
          isActive
            ? 'border-amber-500 bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 shadow-amber-500/30'
            : 'border-amber-400/70 bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-800 hover:from-amber-100 hover:to-yellow-100 dark:from-amber-950/40 dark:to-yellow-950/30 dark:text-amber-200 dark:hover:from-amber-900/50 dark:hover:to-yellow-900/40'
        }`}
      >
        <BriefcaseIcon className="w-4 h-4 shrink-0" />
        <span>{isActive ? 'Banker active!' : 'Use banker!'}</span>
        {isActive && <span aria-hidden="true">★</span>}
      </button>

      {!isActive && (
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-48 sm:w-max max-w-[calc(100vw-2rem)] sm:max-w-xs whitespace-normal text-center"
        >
          {tooltipText}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
        </div>
      )}
    </div>

    {isActive && (
      <p
        id="banker-explanation"
        className="mt-2 px-3 py-2 text-xs text-center leading-relaxed rounded-lg border border-amber-300/60 bg-amber-50/80 text-amber-900 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-100 max-w-xs"
      >
        {tooltipText}
      </p>
    )}
  </div>
);

export default BankerButton;
